import type { RequestOptions } from '../client.js';
import type { ChainSlug, EvmChainSlug, StarknetChainSlug } from '../types/chains.js';
import type { HistoricalQueryOptions } from '../types/client.js';
import {
  type ReadContractParams,
  type SimulateContractParams,
  type ContractAbiFunction,
  type ContractCodeData,
  readContractParamsSchema,
  multicallParamsSchema,
  simulateContractParamsSchema,
  getCodeParamsSchema,
} from '@spectrum-nodes/api-spec/models';
import type { MulticallResult, SimulateContractResult } from '../types/index.js';
import {
  encodeCall,
  decodeResult,
  parseHumanReadableAbi,
  type AbiFunction,
} from '../internals/abi.js';
import { ValidationError, ApiError } from '../types/errors.js';
import { parseParams } from '../internals/validation.js';
import { splitHistoricalArgs } from '../internals/utils.js';
import { normalizeBlockTag } from '../internals/block-tag.js';
import { ChainNamespace } from './base.js';

/**
 * `eth_call` / `eth_estimateGas` envelope returned via `rpcProxy`.
 *
 * This is a contracts-namespace narrowing of api-spec's `RpcResponse`
 * (which keeps `result` / `error.data` as `unknown` for the general
 * JSON-RPC case). Here we commit to the hex-string shape these specific
 * methods always return — `result` is the ABI-encoded hex output, and
 * `error.data` is hex-encoded revert data. `id` is only populated on
 * batch responses (single calls don't echo it back).
 */
type EthCallResponse = {
  jsonrpc: string;
  id?: number;
  result?: string;
  error?: { code: number; message: string; data?: string };
};

/** Smart contract read operations via RPC proxy */
export class ContractsNamespace extends ChainNamespace {
  private async executeRpcBatch(
    chain: string,
    requests: Array<{ method: string; params: unknown[]; id: number }>,
    options?: RequestOptions,
  ): Promise<EthCallResponse[]> {
    return this.client.rpcProxyBatch(chain, requests, { ...options, cacheTtl: 0 }) as Promise<
      EthCallResponse[]
    >;
  }

  /**
   * Call a read-only (view/pure) function on a smart contract.
   * ABI-encodes the call, sends eth_call via the RPC proxy, and decodes the result.
   */
  async readContract<T = unknown>(
    chain: ChainSlug | undefined,
    params: ReadContractParams,
    options?: RequestOptions,
  ): Promise<T> {
    const resolvedChain = this.resolve(chain);
    const validated = parseParams(readContractParamsSchema, params);

    const fn = this.findFunction(validated.abi, validated.functionName);
    const calldata = encodeCall(fn, validated.args ?? []);

    const blockTag = normalizeBlockTag(validated.blockTag);

    const response = (await this.client.rpcProxy(
      resolvedChain,
      { method: 'eth_call', params: [{ to: validated.address, data: calldata }, blockTag] },
      { ...options, cacheTtl: 0 },
    )) as EthCallResponse;

    if (response.error) {
      throw new ApiError(
        `Contract call failed: ${response.error.message}`,
        502,
        this.client.endpointUrl(),
        response.error,
      );
    }

    if (!response.result || response.result === '0x') {
      throw new ApiError(
        'Contract call returned empty result (contract may not exist or function reverted)',
        502,
        this.client.endpointUrl(),
      );
    }

    const decoded = decodeResult(fn.outputs, response.result);

    if (fn.outputs.length === 1) {
      return decoded[0] as T;
    }
    return decoded as T;
  }

  /**
   * Execute multiple contract reads in a single batch RPC call.
   */
  async multicall(
    chain: ChainSlug | undefined,
    params: { calls: ReadContractParams[] },
    options?: RequestOptions,
  ): Promise<MulticallResult[]> {
    const resolvedChain = this.resolve(chain);
    const validated = parseParams(multicallParamsSchema, params);

    const resolvedFns = validated.calls.map((call) =>
      this.findFunction(call.abi, call.functionName),
    );

    const batchBody = validated.calls.map((call, i) => {
      const calldata = encodeCall(resolvedFns[i], call.args ?? []);
      const blockTag = normalizeBlockTag(call.blockTag);

      return {
        jsonrpc: '2.0' as const,
        method: 'eth_call',
        params: [{ to: call.address, data: calldata }, blockTag],
        id: i + 1,
      };
    });

    const responses = await this.executeRpcBatch(
      resolvedChain,
      batchBody.map((request) => ({
        method: request.method,
        params: request.params as unknown[],
        id: request.id,
      })),
      options,
    );

    return validated.calls.map((_call, i) => {
      const resp = responses.find((r) => r.id === i + 1);
      if (!resp) {
        return { status: 'error' as const, error: `No response for call ${i + 1}` };
      }
      if (resp.error) {
        return { status: 'error' as const, error: resp.error.message };
      }
      if (!resp.result || resp.result === '0x') {
        return { status: 'error' as const, error: 'Empty result' };
      }
      try {
        const fn = resolvedFns[i];
        const decoded = decodeResult(fn.outputs, resp.result);
        const result = fn.outputs.length === 1 ? decoded[0] : decoded;
        return { status: 'success' as const, result };
      } catch (err) {
        return { status: 'error' as const, error: (err as Error).message };
      }
    });
  }

  /**
   * Simulate a contract write call without sending a transaction.
   * Uses eth_call with full tx params to check if a write would succeed,
   * and eth_estimateGas to estimate gas cost.
   */
  async simulateContract<T = unknown>(
    chain: ChainSlug | undefined,
    params: SimulateContractParams,
    options?: RequestOptions,
  ): Promise<SimulateContractResult<T>> {
    const resolvedChain = this.resolve(chain);
    const validated = parseParams(simulateContractParamsSchema, params);

    const fn = this.findFunction(validated.abi, validated.functionName);
    const calldata = encodeCall(fn, validated.args ?? []);

    const blockTag = normalizeBlockTag(validated.blockTag);

    const txObj: Record<string, string> = {
      to: validated.address,
      data: calldata,
    };
    if (validated.from) {
      txObj.from = validated.from;
    }
    if (validated.value) {
      txObj.value = validated.value;
    }
    if (validated.gas) {
      txObj.gas = validated.gas;
    }

    const batchBody = [
      { jsonrpc: '2.0', method: 'eth_call', params: [txObj, blockTag], id: 1 },
      { jsonrpc: '2.0', method: 'eth_estimateGas', params: [txObj, blockTag], id: 2 },
    ];

    const responses = await this.executeRpcBatch(
      resolvedChain,
      batchBody.map((request) => ({
        method: request.method,
        params: request.params as unknown[],
        id: request.id,
      })),
      options,
    );

    const callResponse = responses.find((r) => r.id === 1);
    const gasResponse = responses.find((r) => r.id === 2);

    if (!callResponse) {
      throw new ApiError(
        'simulateContract: missing eth_call response in batch reply',
        502,
        this.client.endpointUrl(),
        responses,
      );
    }

    if (callResponse.error) {
      return {
        success: false,
        error: callResponse.error.data
          ? `${callResponse.error.message} (data: ${callResponse.error.data})`
          : callResponse.error.message,
      };
    }

    let decoded: T | undefined;
    if (callResponse.result && callResponse.result !== '0x' && fn.outputs.length > 0) {
      const values = decodeResult(fn.outputs, callResponse.result);
      decoded = (fn.outputs.length === 1 ? values[0] : values) as T;
    }

    let gasUsed: bigint | undefined;
    if (gasResponse?.result) {
      gasUsed = BigInt(gasResponse.result);
    }

    return {
      success: true,
      result: decoded,
      gasUsed,
    };
  }

  /**
   * Check if an address is a contract and get its bytecode (Starknet: its class). EVM + Starknet.
   */
  async getCode(
    chain: EvmChainSlug | StarknetChainSlug | undefined,
    address: string,
    paramsOrOptions?: HistoricalQueryOptions | RequestOptions,
    options?: RequestOptions,
  ): Promise<ContractCodeData> {
    const { params, options: requestOptions } = splitHistoricalArgs(paramsOrOptions, options);
    const validated = parseParams(getCodeParamsSchema, {
      chain: this.resolve(chain),
      address,
      blockHeight: params?.blockHeight,
    });
    return this.client.jsonRpc<ContractCodeData>('getCode', validated, requestOptions);
  }

  private findFunction(abi: (ContractAbiFunction | string)[], functionName: string): AbiFunction {
    for (const entry of abi) {
      if (typeof entry === 'string') {
        try {
          const parsed = parseHumanReadableAbi(entry);
          if (parsed.name === functionName) {
            return parsed;
          }
        } catch {
          // skip unparseable entries
        }
      } else if (
        entry.name === functionName &&
        (entry.type === 'function' || entry.type === undefined)
      ) {
        return {
          name: entry.name,
          inputs: entry.inputs ?? [],
          outputs: entry.outputs ?? [],
        };
      }
    }
    throw new ValidationError('functionName', `Function "${functionName}" not found in ABI`);
  }
}
